import { Suspense, useRef, useState } from 'react';
import { Outlet } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAgency, type SuspendedAgency } from '@/auth/AgencyProvider';
import { appRelease } from '@/lib/appVersion';
import { NoAgencyScreen, AgencyErrorBanner, PendingInvitesBanner } from '@/components/common/NoAgencyScreen';
import { SuspendedAgencyScreen } from '@/components/common/SuspendedAgencyScreen';
import { PastDueBanner } from '@/components/billing/PastDueBanner';
import { useBillingRequest, useMyBillingStatus } from '@/data/useBilling';
import { useRpcErrorMessage } from '@/lib/rpcErrors';
import { ProfileBanner } from '@/components/profile/ProfileBanner';
import { AnnouncementBanner } from '@/components/common/AnnouncementBanner';
import { RouteFallback } from '@/components/common/RouteFallback';
import { Toaster } from '@/components/ui/sonner';
import { Sidebar } from './Sidebar';
import { Topbar } from './Topbar';
import { MobileNav } from './MobileNav';
import { Wordmark } from './Wordmark';
import { BareLayout } from './BareLayout';

/**
 * Fixed rail on the left, top bar, white cards on the grey canvas, dark brand footer.
 *
 * The rail is `sticky`, not `fixed`, and the page is a flex COLUMN whose last child is the footer.
 * That is what lets the navy footer run the whole width, under the rail as well, the way the mockup
 * ends the screen: a fixed rail is glued to the viewport and would paint straight over it.
 */
export function AppLayout() {
  const { t } = useTranslation();
  const agency = useAgency();
  // Below md the rail is a drawer (portal-mobile); the hamburger that opens it lives in the top bar.
  const [navOpen, setNavOpen] = useState(false);
  const menuButton = useRef<HTMLButtonElement>(null);

  // Every membership in a suspended agency: nothing of the portal would load against zero agencies, so
  // no rail, no menu, no top bar. Only the paused screen, the language switch and the sign-out.
  if (agency.status === 'suspended') return <SuspendedLayout agencies={agency.agencies} />;
  // F62: no active membership (a driver, a removed member, an invitee): no office menu that would lead to
  // empty pages. The bare chrome (language, sign-out) and the screen that says why.
  if (agency.status === 'none') {
    return (
      <BareLayout>
        <NoAgencyScreen />
      </BareLayout>
    );
  }

  return (
    <div className="flex min-h-screen flex-col bg-canvas">
      <div className="flex flex-1">
        {/* The navy belongs to the COLUMN, not to the sticky rail inside it: once the page is taller
            than the viewport the rail is only one screen tall, and without this the canvas would show
            through underneath it. */}
        <div data-testid="desktop-rail" className="hidden w-rail shrink-0 bg-rail md:block">
          <Sidebar />
        </div>
        <MobileNav open={navOpen} onOpenChange={setNavOpen} returnFocusTo={menuButton} />
        <div className="flex min-w-0 flex-1 flex-col">
          <Topbar menu={{ open: navOpen, onOpen: () => setNavOpen(true), buttonRef: menuButton }} />
          <main className="flex-1 p-4 sm:p-6">
            {/* A read error does NOT block the portal: the banner offers a retry and gated actions stay
                hidden (no role = nothing allowed). */}
            {
              <>
                {agency.status === 'error' && <AgencyErrorBanner onRetry={agency.reload} />}
                {agency.status === 'ready' && <PendingInvitesBanner />}
                {/* agency-billing: a past-due payment, for whoever can pay it. */}
                {agency.status === 'ready' && <PastDueBanner />}
                {/* operator-panel-v2 T2.7: the operator's notices (my_announcements), once the agency is known. */}
                <AnnouncementBanner enabled={agency.status === 'ready'} />
                <ProfileBanner />
                {/* Screens are lazy chunks (App.tsx): the layout stays while one downloads. */}
                <Suspense fallback={<RouteFallback />}>
                  <Outlet />
                </Suspense>
              </>
            }
          </main>
        </div>
      </div>

      <footer className="flex flex-wrap items-center justify-between gap-x-6 gap-y-2 bg-rail px-4 py-3 sm:px-6">
        <div className="flex items-center gap-2">
          <Wordmark tone="rail" size="sm" layout="inline" />
          <span className="rounded-full bg-rail-2 px-2 py-0.5 text-xs font-medium text-on-rail-muted">
            {t('brand.version', { version: appRelease() })}
          </span>
        </div>
        <p className="text-sm text-on-rail-muted">{t('footer.tagline')}</p>
        <p className="text-sm text-on-rail-muted">{t('footer.attribution')}</p>
      </footer>
      <Toaster />
    </div>
  );
}

/** The whole page while access is paused: the bare chrome, then the paused card. No navigation at all
 *  (change spec operator-agency-onboarding, design 2). agency-billing (F9): the caller's billing rows
 *  decide whether the card offers a way to pay; "Pay now" without an invoice link asks for the page the
 *  server named in `pay_with` (the Stripe billing portal, or Checkout: billing-followups 0170) and leaves
 *  for it. */
function SuspendedLayout({ agencies }: { agencies: SuspendedAgency[] }) {
  const { rows } = useMyBillingStatus();
  const pay = useBillingRequest();
  const message = useRpcErrorMessage();
  return (
    <BareLayout>
      <SuspendedAgencyScreen
        agencies={agencies}
        billing={rows}
        onPay={(agencyId, kind) => void pay.request(kind, agencyId)}
        payBusy={pay.busy}
        payError={pay.error ? message(pay.error) : null}
      />
    </BareLayout>
  );
}
